// Use of restricted syntax (window.localStorage) is intentional.
/* eslint-disable no-restricted-syntax */
import * as Sentry from '@sentry/react';

const isAvailable = (function () {
  let isAvailable0: boolean | undefined;

  // Lesser used browsers have strange fallback behaviours when localStorage isn't available, so we'll feature detect it
  // by trying to use it and verifying that it works instead of checking if it has the right shape.

  function isAvailableImpl() {
    try {
      if (typeof window.localStorage === 'undefined' || window.localStorage === null) {
        return false;
      }
      const key = '__localStorageEx__';
      const expected = String(Date.now());
      window.localStorage.setItem(key, expected);
      const actual = window.localStorage.getItem(key);
      window.localStorage.removeItem(key);
      return actual === expected;
    } catch {
      return false;
    }
  }

  return function isAvailableMemo() {
    if (isAvailable0 == null) {
      isAvailable0 = isAvailableImpl();
    }
    return isAvailable0;
  };
})();

type ReviverFunc<T> = (item: string) => T;

type SerializeFunc<T> = (value: T) => string;

const IgnoredMessages = new Set([
  "Cannot read property 'getItem' of null",
  "TypeError: Cannot read properties of null (reading 'getItem')",
  "Cannot read properties of null (reading 'getItem')",
  "Cannot read property 'setItem' of null",
  "TypeError: Cannot read properties of null (reading 'setItem')",
  "Cannot read properties of null (reading 'setItem')",
  "Cannot read property 'removeItem' of null",
  "TypeError: Cannot read properties of null (reading 'removeItem')",
  "Cannot read property 'localStorage' of null",
  "TypeError: Cannot read properties of null (reading 'localStorage')",
  'window.localStorage is null',
  // safari error messages:
  "null is not an object (evaluating 'window.localStorage.getItem')",
  "null is not an object (evaluating 'window.localStorage.setItem')",
  "null is not an object (evaluating 'window.localStorage.removeItem')",
]);

// Firefox-specific error names thrown when localStorage is unavailable or the operation is aborted.
const IgnoredNames = new Set(['NS_ERROR_FAILURE', 'NS_ERROR_ABORT']);

/**
 * Gets an item from localStorage.
 *
 * @template T Value type.
 * @param key Key to store at.
 * @param deserializeFunc Converts the item from {@link String} to {@link T}, e.g. {@link JSON.parse} or {@link String}.
 * @param valueIfNotFound Value to return if no item exists.
 * @param valueIfNotAvailable Value to return if localStorage is not available. Defaults to {@link valueIfNotFound}.
 * @param valueIfNotDeserialized Value to return if conversion from {@link String} to {@link T} fails. Default to {@link valueIfNotFound}.
 * @returns Stored value or the appropriate default value.
 */
function getItem<T>(
  key: string,
  deserializeFunc: ReviverFunc<T>,
  valueIfNotFound: T,
  valueIfNotAvailable: T = valueIfNotFound,
  valueIfNotDeserialized: T = valueIfNotFound
): T {
  if (!isAvailable()) {
    return valueIfNotAvailable;
  }

  let item: string | null;

  try {
    item = window.localStorage.getItem(key);
  } catch (err) {
    if (!IgnoredMessages.has(err?.message) && !IgnoredNames.has(err?.name)) {
      Sentry.captureException(err);
    }
    return valueIfNotAvailable;
  }

  if (item == null) {
    return valueIfNotFound;
  }

  try {
    return deserializeFunc(item);
  } catch (err) {
    if (!IgnoredMessages.has(err?.message)) {
      Sentry.captureException(err);
    }
    return valueIfNotDeserialized;
  }
}

/**
 * Gets a JSON item from localStorage, returning {@link valueIfInvalid} unless the stored value
 * satisfies {@link isValid}.
 *
 * Prefer this over {@link getItem} with {@link JSON.parse}. localStorage is user-writable and can
 * still hold data written by an older release, so `JSON.parse` returns `any` and {@link getItem}
 * infers `T` from the fallback — the declared type is an assumption, never a check. Passing a type
 * guard here makes the boundary honest, so corrupted or outdated data yields the fallback instead
 * of a value that lies about its shape and crashes downstream.
 *
 * @template T Value type.
 * @param key Key to read from.
 * @param isValid Type guard the parsed value must satisfy.
 * @param valueIfInvalid Value to return if the item is absent, unparseable, or fails {@link isValid}.
 * Unparseable data is additionally reported to Sentry by {@link getItem}; a value that parses but
 * fails {@link isValid} is not, since outdated or foreign data is expected here.
 * @returns The stored value, or {@link valueIfInvalid}.
 */
function getItemValidated<T>(key: string, isValid: (value: unknown) => value is T, valueIfInvalid: T): T {
  const value = getItem<unknown>(key, JSON.parse, undefined);
  return isValid(value) ? value : valueIfInvalid;
}

/**
 * Removes an item from localStorage.
 *
 * @param key Key to remove at.
 */
function removeItem(key: string) {
  if (!isAvailable()) {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    // Not much we can do about this.
    if (!IgnoredMessages.has(err?.message) && !IgnoredNames.has(err?.name)) {
      Sentry.captureException(err);
    }
  }
}

/**
 * Stores an item to localStorage.
 *
 * @template T Value type.
 * @param key Key to store at.
 * @param value Value to store.
 * @param serializeFunc Converts the value from {@link T} to {@link string}, e.g. {@link JSON.stringify} or {@link String}.
 * @param removeIfQuotaExceeded When true, the item at the key is removed if storing exceeds the quota. Defaults to true.
 * @param logToSentryIfQuotaExceeded When true, logs a message to Sentry if storing exceeds the quota. Defaults to false.
 */
function setItem<T>(
  key: string,
  value: T,
  serializeFunc: SerializeFunc<T>,
  removeIfQuotaExceeded = true,
  logToSentryIfQuotaExceeded = false
) {
  if (!isAvailable()) {
    return;
  }

  const item = serializeFunc(value);

  try {
    window.localStorage.setItem(key, item);
  } catch (err) {
    const isQuotaExceeded =
      err instanceof DOMException &&
      (err.name === 'QuotaExceededError' || err.name === 'QUOTA_EXCEEDED_ERR' || err.code === 22);
    if (isQuotaExceeded) {
      if (removeIfQuotaExceeded) {
        removeItem(key);

        if (logToSentryIfQuotaExceeded) {
          Sentry.captureMessage('localStorage quota exceeded. Removing item.', { extra: { error: err, key, item } });
        }
      }
    } else if (!IgnoredMessages.has(err?.message) && !IgnoredNames.has(err?.name)) {
      Sentry.captureException(err);
    }
  }
}

// helper to clear product-specific keys that usually start with a prefix
function clearAllKeysWithPrefix(prefix = '') {
  if (!isAvailable() || !prefix) {
    return;
  }

  Object.keys(window.localStorage).forEach((key) => {
    if (key.startsWith(prefix)) {
      window.localStorage.removeItem(key);
    }
  });
}

export default {
  getItem,
  getItemValidated,
  setItem,
  removeItem,
  isAvailable,
  clearAllKeysWithPrefix,
};
